import api from './api';

export interface Tenant {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
  is_blocked: boolean;
  blocked_at?: string;
  blocked_by?: number;
  blocked_by_name?: string;
  block_reason?: string;
  description?: string;
  address?: string;
  contact_email?: string;
  contact_phone?: string;
  logo?: string;
  created_at: string;
  updated_at?: string;
  sites_count?: number;
  users_count?: number;
  admin_info?: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_active: boolean;
    site_name: string;
    site_code: string;
  };
  sites_info?: Array<{
    id: number;
    name: string;
    code: string;
    site_type: string;
    address: string;
    contact_email: string;
    contact_phone: string;
    created_at: string;
  }>;
  users_info?: Array<{
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_active: boolean;
    roles: Array<{
      id: number;
      name: string;
      display_name: string;
      site_name: string;
      site_code: string;
      is_default: boolean;
    }>;
    sites: Array<{
      id: number;
      name: string;
      code: string;
    }>;
  }>;
  users?: Array<{
    id: number;
    username: string;
    email: string;
    full_name: string;
    roles: Array<{
      id: number;
      name: string;
      display_name: string;
    }>;
  }>;
}

export interface CreateTenantData {
  tenant: {
    name: string;
    code: string;
    description?: string;
    address?: string;
    contact_email?: string;
    contact_phone?: string;
  };
  admin: {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    password: string;
  };
  default_site: {
    name: string;
    code: string;
    site_type: string;
    address?: string;
    contact_email?: string;
    contact_phone?: string;
  };
}

export interface CreateTenantResponse {
  tenant: Tenant;
  admin: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_active: boolean;
  };
  default_site: {
    id: number;
    name: string;
    code: string;
    site_type: string;
    is_active: boolean;
  };
  email_sent: boolean;
}

class TenantService {
  private baseUrl = '/platform/tenants/';

  /**
   * Récupérer la liste de tous les tenants
   */
  async getTenants(): Promise<Tenant[]> {
    try {
      const response = await api.get(this.baseUrl);
      
      const tenants = response.data.results || response.data.data || response.data;
      
      return tenants;
    } catch (error: any) {
      console.error('❌ Erreur lors de la récupération des tenants:', error);
      console.error('❌ Détails de l\'erreur API:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des tenants');
    }
  }

  /**
   * Récupérer un tenant par son ID
   */
  async getTenant(id: number): Promise<Tenant> {
    try {
      const response = await api.get(`${this.baseUrl}${id}/`);
      return response.data.data || response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération du tenant:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération du tenant');
    }
  }

  /**
   * Créer un nouveau tenant avec son administrateur
   */
  async createTenant(data: CreateTenantData): Promise<CreateTenantResponse> {
    try {
      const response = await api.post(this.baseUrl, data);
      return response.data.data || response.data;
    } catch (error: any) {
      console.error('Erreur lors de la création du tenant:', error);
      
      // Gestion des erreurs de validation
      if (error.response?.status === 400) {
        const errors = error.response.data;
        if (typeof errors === 'object') {
          const errorMessages = Object.values(errors).flat();
          throw new Error(`Erreur de validation: ${errorMessages.join(', ')}`);
        }
      }
      
      throw new Error(error.response?.data?.message || 'Erreur lors de la création du tenant');
    }
  }

  /**
   * Mettre à jour un tenant
   */
  async updateTenant(id: number, data: Partial<Tenant>): Promise<Tenant> {
    try {
      const response = await api.put(`${this.baseUrl}${id}/`, data);
      return response.data.data || response.data;
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du tenant:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la mise à jour du tenant');
    }
  }

  /**
   * Supprimer un tenant (soft delete - désactivation)
   */
  async deleteTenant(id: number): Promise<void> {
    try {
      await api.delete(`${this.baseUrl}${id}/`);
    } catch (error: any) {
      console.error('Erreur lors de la suppression du tenant:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la suppression du tenant');
    }
  }

  /**
   * Désactiver un tenant (soft delete)
   */
  async deactivateTenant(id: number, reason?: string): Promise<any> {
    try {
      
      const response = await api.post(`${this.baseUrl}${id}/deactivate/`, { reason });
      
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la désactivation du tenant:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la désactivation du tenant');
    }
  }

  /**
   * Supprimer définitivement un tenant (hard delete)
   */
  async hardDeleteTenant(id: number, reason?: string): Promise<any> {
    try {
      const response = await api.post(`${this.baseUrl}${id}/delete/`, { reason });
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la suppression définitive du tenant:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la suppression définitive du tenant');
    }
  }

  /**
   * Vérifier les dépendances d'un tenant avant suppression
   */
  async getTenantDependencies(id: number): Promise<any> {
    try {
      const response = await api.get(`${this.baseUrl}${id}/dependencies/`);
      return response.data.data || response.data;
    } catch (error: any) {
      console.error('Erreur lors de la vérification des dépendances:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la vérification des dépendances');
    }
  }

  /**
   * Bloquer un tenant
   */
  async blockTenant(id: number, reason?: string): Promise<Tenant> {
    try {
      const response = await api.post(`${this.baseUrl}${id}/block/`, { reason });
      return response.data.data || response.data;
    } catch (error: any) {
      console.error('Erreur lors du blocage du tenant:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors du blocage du tenant');
    }
  }

  /**
   * Débloquer un tenant
   */
  async unblockTenant(id: number): Promise<Tenant> {
    try {
      const response = await api.post(`${this.baseUrl}${id}/unblock/`);
      return response.data.data || response.data;
    } catch (error: any) {
      console.error('Erreur lors du déblocage du tenant:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors du déblocage du tenant');
    }
  }

  /**
   * Récupérer les statistiques d'un tenant
   */
  async getTenantStatistics(id: number): Promise<any> {
    try {
      const response = await api.get(`${this.baseUrl}${id}/statistics/`);
      return response.data.data || response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des statistiques');
    }
  }

  /**
   * Récupérer les utilisateurs d'un tenant
   */
  async getTenantUsers(id: number): Promise<any[]> {
    try {
      const response = await api.get(`${this.baseUrl}${id}/users/`);
      return response.data.data || response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des utilisateurs');
    }
  }
}

const tenantService = new TenantService();
export default tenantService;
